import {AssistantContextSnapshot} from '@prompt-bridge/shared/ai-assistant-core';

import {AssistantContextPatch} from '../types/AssistantContextPatch';
import {EMPTY_SNAPSHOT} from './EMPTY_SNAPSHOT';
import {logAiAssistantStoreSet} from './logAiAssistantStoreSet';

/**
 * Closure-factory for the app-context store: an external module-level store (not React Context or a
 * ref) so non-React producers (RTK middleware, window listeners) can write it too. `patchContext`
 * writes per key through a Proxy whose `set` trap logs each write in dev (see
 * {@link logAiAssistantStoreSet}). Each patch replaces the snapshot reference and notifies
 * subscribers while preserving send-time reads from the latest snapshot. The factory gives tests
 * an isolated instance.
 */
export const createAiAssistantContextStore = () => {
  type Listener = () => void;
  type SendSynchronizer = () => void;
  const listeners = new Set<Listener>();
  const sendSynchronizers = new Set<SendSynchronizer>();

  const createSnapshot = (target: AssistantContextSnapshot) =>
    new Proxy(target, {
      set(object, key, value, receiver) {
        logAiAssistantStoreSet(String(key), value);
        return Reflect.set(object, key, value, receiver);
      },
    });

  let snapshot = createSnapshot({...EMPTY_SNAPSHOT});

  const getContext = () => snapshot;

  const getContextForSend = () => {
    [...sendSynchronizers].forEach((synchronize) => synchronize());
    return snapshot;
  };

  const patchContext = (patch: AssistantContextPatch) => {
    const nextSnapshot = createSnapshot({...snapshot});
    Object.assign(nextSnapshot, patch);
    snapshot = nextSnapshot;
    listeners.forEach((listener) => listener());
  };

  const subscribe = (listener: Listener) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  const registerSendSynchronizer = (synchronize: SendSynchronizer) => {
    sendSynchronizers.add(synchronize);
    return () => {
      sendSynchronizers.delete(synchronize);
    };
  };

  return {getContext, getContextForSend, patchContext, registerSendSynchronizer, subscribe};
};
